import React, {Component} from 'react'
import Visibility from 'visibilityjs'
import axios from "axios"

import PDFReader from './PDFReader'
import PDFControls from './PDFControls'
import Privacy from './Privacy'
import EnhancedEvent from '../helpers/EnhancedEvent'
import '../scss/PDFViewer.scss'


class PDFViewer extends Component {
    constructor(props) {
        super(props)
        this.state = {
            document: {},
            metadata: {},
            documentLoaded: false,
            title: props.title || this.getFilename(),
            currentPage: 1,
            scale: 1,
            maximumPagePosition: -1,
            ratingOptions: this.props.ratingOptions,
            documentBlob: { url: null, blob: null }
        }

        document.title = this.state.title
        this.timeOutsArray = {};
        this.addEnhancedEvent('PDF_VIEWER_INIT_START', this.state.currentPage, undefined);
    }

    getFilename() {
        try {
            const url = new URL(this.props.uri)
            return decodeURI(url.pathname.split('/').pop())
        } catch (e) {
            return null
        }
    }

    componentDidMount() {
        this.addEnhancedEvent('PDF_VIEWER_MOUNT', this.state.currentPage, undefined);

        window.addEventListener('copy', this.handleCopy);
        window.addEventListener('cut', this.handleCopy);

        if (window.onbeforeprint !== undefined && window.onafterprint !== undefined) {
            window.addEventListener('beforeprint', (e) => {
                this.doPrintOrDownload('printContainer');
            });

            window.addEventListener('afterprint', (e) => {
                this.addEnhancedEvent('PRINT', this.state.currentPage, undefined);

                const viewer = document.querySelector('.PDFViewer');
                if (viewer) {
                    viewer.classList.remove('disable-printing');
                }
            });
        }

        document.addEventListener('keydown', (e) => {
            if (!e.metaKey && !e.ctrlKey) return;

            if (e.keyCode === 187) {
                e.preventDefault()
                this.handleZoomIn()
            } else if (e.keyCode === 189) {
                e.preventDefault()
                this.handleZoomOut()
            } else if (e.key === '0') {
                this.handleZoomReset()
            } else if (!this.props.viewerPermissions.copy && (e.key === "c" || e.key === "x" || e.key === "v")) {
                e.preventDefault();
                return false;
            } else if (e.key === "p") {
                e.preventDefault();

                if (this.props.viewerPermissions.print) {
                    this.handlePrintClick();
                } else {
                    return false;
                }
            } else if (e.key === "s") {
                e.preventDefault();

                if (this.props.viewerPermissions.download) {
                    this.handleDownloadClick();
                } else {
                    return false;
                }
            }
        })

        this.addEnhancedEvent('OPEN', this.state.currentPage, undefined);

        Visibility.change((e, state) => {
            const visible = !Visibility.hidden()
            this.props.onVisibilityChange(visible, {
                page: this.state.currentPage
            })

            if (visible && this.state.documentLoaded) {
                this.startTicks()
            } else {
                this.stopTicks()
            }
        })

        window.addEventListener('blur', () => {
            this.props.onBlur({
                page: this.state.currentPage
            })
        })

        window.addEventListener('focus', () => {
            this.props.onFocus({
                page: this.state.currentPage
            })
        })

        window.addEventListener('offline', () => {
            this.props.onOffline({
                page: this.state.currentPage
            })
        })

        window.addEventListener('online', () => {
            this.props.onOnline({
                page: this.state.currentPage
            })
        })

        window.addEventListener('beforeunload', (e) => {
            this.props.onBeforeUnload(e, {
                page: this.state.currentPage
            })
        })

        window.addEventListener('unload', (e) => {
            this.props.onUnload(e, {
                page: this.state.currentPage
            })
        })
    }

    handleZoomIn = () => {
        const scale = this.state.scale * 1.1
        this.setState({
            scale: scale
        })

        this.handleZoom(scale)
    }

    handleZoomOut = () => {
        const scale = this.state.scale / 1.1
        this.setState({
            scale: scale
        })

        this.handleZoom(scale)
    }

    handleZoomReset = () => {
        const scale = 1
        this.setState({
            scale: scale
        })

        this.handleZoom(scale)
    }

    handleZoom = (scale) => {
        this.addEnhancedEvent('SCALE_CHANGE', this.state.currentPage, { scale: scale });
        window.dispatchEvent(new CustomEvent('zoom'));
    }

    handleDocumentLoaded = (doc) => {
        this.addEnhancedEvent('DOCUMENT_LOAD', this.state.currentPage, undefined);

        doc.getPage(1).then((page) => {
            this.setState({
                viewport:  page.getViewport({scale: 1.0})
            });
        });

        doc.getMetadata().then(metadata => {
            this.setState({
                document: doc,
                metadata: metadata.info,
                documentTitle: metadata.info.Title,
                totalPages: doc.numPages
            })

            let title = this.state.title

            if (!title) {
                title = metadata.info.Title
                this.setState({
                    title: title
                })
            }

            document.title = title

            this.addEnhancedEvent(
                'METADATA_LOAD',
                this.state.currentPage,
                { metadata: { title: metadata.info.Title, totalPages: doc.numPages } }
            );

            this.setState({
                documentLoaded: true
            })

            this.startTicks()
        })
    }

    handlePagesInit = () => {
        const readerElement = this.pdfReader.readerElement
        this.pdfReaderScrollbarWidth = readerElement.offsetWidth - readerElement.clientWidth
        window.dispatchEvent(new Event('resize'));
    }

    startTicks() {
        this.stopTicks()
        this.tickInterval = setInterval(() => {
            this.addEnhancedEvent('TICK', this.state.currentPage, { totalPages: this.state.document.numPages });
        }, 5000)
    }

    stopTicks() {
        clearInterval(this.tickInterval)
    }

    addEnhancedEvent = (eventType, page, extraParams) => {
        let eventConfig = { type: eventType, page: page };

        if (extraParams) {
            eventConfig = Object.assign(eventConfig, extraParams);
        }

        this.props.enhancedEvents.add(new EnhancedEvent(eventConfig))
    }

    handlePageChange = (pageTo) => {
        if (pageTo === this.state.currentPage) return;
        this.setState({ currentPage: pageTo });
        this.addEnhancedEvent('PAGE_CHANGE', pageTo, undefined);
    }

    handleScroll = (maximumPagePosition) => {
        this.setState({
            maximumPagePosition: maximumPagePosition
        })

        this.addEnhancedEvent('SCROLL', this.state.currentPage, { maximumPagePosition: maximumPagePosition });
    }

    handleToolbarPageChange = (pageTo) => {
        this.setState({
            currentPage: pageTo,
            toolbarSetPage: pageTo
        })
    }

    handleAttributionClick = () => {
        this.addEnhancedEvent('ATTRIBUTION_FOLLOW', this.state.currentPage, undefined);
    }

    handleLoadingProgress = (percentage) => {
        this.setState({
            loadingProgress: percentage
        })
    }

    openDefaultPDFView() {
        if (window.confirm('This operation is not supported by your browser. You will be redirected to the default PDF Reader in order to print the document.')) {
            window.open(this.props.uri, '_blank');
            return true;
        } else {
            return false;
        }
    }

    doPrintOrDownload(elementId) {
        let el = document.getElementById(elementId);
        clearTimeout(this.timeOutsArray[elementId]);
        delete this.timeOutsArray[elementId];

        if (elementId === "printFrame") {
            let hasPrinted = true;

            if (window.navigator && window.navigator.msSaveOrOpenBlob) { // IE or Edge
                if (!!window.MSInputMethodContext && !!document.documentMode) { // IE
                    hasPrinted = this.openDefaultPDFView();
                } else { // Edge
                    window.print();
                }
            } else {
                try {
                    el.src = this.state.documentBlob.url;
                } catch(e) {
                    console.log('Exception thrown while printing.', e);
                    hasPrinted = this.openDefaultPDFView();
                }
            }

            if (hasPrinted) {
                this.addEnhancedEvent('PRINT', this.state.currentPage, undefined);
            }
        } else if (elementId === 'printContainer') {
            this.printDocumentUsingCanvases();
        } else if (elementId === "downloadLink") {
            if (window.navigator && window.navigator.msSaveOrOpenBlob) { // IE
                const filename = el.download !== undefined ? el.download : this.state.title;
                window.navigator.msSaveOrOpenBlob(this.state.documentBlob.blob, filename)
            } else {
                if (window.navigator.vendor === "Apple Computer, Inc.") {
                    el.target = '_self';
                }
                el.href = this.state.documentBlob.url;
                el.click();
            }

            this.addEnhancedEvent('DOWNLOAD', this.state.currentPage, undefined);
        }

        this.closePrintOrDownload(el);
    }

    printDocumentUsingCanvases() {
        const self = this;
        const elementId = 'printContainer';
        let pageWidth = '612';
        let pageHeight = '792';

        if (document.getElementById(elementId) == null) {
            let container = document.createElement('div');
            container.id = elementId;
            document.body.appendChild(container);
        }

        if (document.querySelectorAll('.printContainer-page').length === 0) {
            const container = document.getElementById(elementId);
            const pages = document.querySelectorAll('.canvas-print');

            Array.from(pages).forEach(function(page) {
                let img = document.createElement('img');
                img.src = page.toDataURL();
                img.style.width = (4/3 * self.state.viewport.width) + 'px';
                img.style.height = (4/3 * self.state.viewport.height) + 'px';

                let pageDiv = document.createElement('div');
                pageDiv.classList.add('printContainer-page');
                pageDiv.appendChild(img);
                container.appendChild(pageDiv);

                pageWidth = self.state.viewport.width;
                pageHeight = self.state.viewport.height;
            });

            let pageStyleSheet = document.createElement('style');
            pageStyleSheet.textContent = '@supports ((size:A4) and (size:1pt 1pt)) {' + '@page { size: ' + pageWidth + 'pt ' + pageHeight + 'pt; }' + '}';
            container.appendChild(pageStyleSheet)
        }

        const viewer = document.querySelector('.PDFViewer');
        if (viewer) {
            viewer.classList.add('disable-printing');
        }
    }

    closePrintOrDownload(ch) {
        this.timeOutsArray[ch] = setTimeout(
            function () {
                try {
                    document.body.removeChild(ch);
                } catch (e) {
                    console.log("Error while closing print or download", e);
                }
            },
            600000
        );
    }

    fetchDocument(elementId) {
        if (!this.props.uri) return;
        const self = this;

        axios({
            method: 'get',
            url: this.props.uri,
            responseType: 'blob'
        }).then(resp => {
            const blob = new Blob([resp.data], {type: 'application/pdf'});

            self.setState(prevState => ({
                documentBlob: {
                    url: URL.createObjectURL(blob),
                    blob: blob
                }
            }));

            self.doPrintOrDownload(elementId);
        }).catch(err => {
            console.log('Error fetching and original document from S3.', err);
        });
    }

    handlePrintClick = (e) => {
        const frameId = "printFrame";

        if (document.getElementById(frameId) == null) {
            let frame = document.createElement("iframe");
            frame.id = frameId;
            frame.classList.add('printFrame');
            document.body.appendChild(frame);
            frame.onload = () => {
                if (frame.src && frame.src !== '') {
                    frame.contentWindow.document.execCommand("print", false, null);
                }
            }
        }

        if (this.state.documentBlob.url == null || this.state.documentBlob.blob == null) {
            this.fetchDocument(frameId);
        } else {
            this.doPrintOrDownload(frameId);
        }
    }

    handleDownloadClick = (e) => {
        const linkId = "downloadLink";

        if (document.getElementById(linkId) == null) {
            let link = document.createElement('a');
            link.id = linkId;
            link.style.display = "none";
            link.target = "_blank";
            link.setAttribute("download", this.state.title);
            document.body.appendChild(link);
        }

        if (this.state.documentBlob.url == null || this.state.documentBlob.blob == null) {
            this.fetchDocument(linkId);
        } else {
            this.doPrintOrDownload(linkId);
        }
    }

    handleCopy = (e) => {
        if (!this.props.viewerPermissions.copy) {
            e.preventDefault();
            return false;
        }

        this.addEnhancedEvent('COPY_PASTE', this.state.currentPage, undefined);
    }

    handleRelatedResearchClick = (e) => {
        this.addEnhancedEvent('RELATED_RESEARCH', this.state.currentPage, undefined);
    }

    handleRatingClick = (score) => {
        let eventScore = 1;
        if (this.props.ratingOptions.type === 'thumb') {
            if (score === 3) {
                eventScore = 5;
            } else if (score === 2) {
                eventScore = 3;
            }
        } else {
            eventScore = score;
        }
        this.addEnhancedEvent('RATING', this.state.currentPage, { additionalData: eventScore });

        let tmpOptions = this.state.ratingOptions;
        tmpOptions.currentScore = score;
        tmpOptions.rated = true;
        this.setState({ratingOptions: tmpOptions});

        if (this.state.ratingOptions.type === "star") {
            for (let i = 1; i <= score; i++) {
                document.getElementsByClassName(i.toString())[0].classList.add('tmp-rating');
            }
        } else {
            document.getElementsByClassName(score.toString())[0].classList.add('tmp-rating');
        }

        setTimeout(function() {
            Array.from(document.getElementsByClassName("rating-thumb")).forEach(function(rc) {
                rc.classList.remove("tmp-rating");
            })
            Array.from(document.getElementsByClassName("rating-star")).forEach(function(rc) {
                rc.classList.remove("tmp-rating");
            })
        }, 2000)
    }

    getDocPageWidth = () => {
        if (!this.pdfReader || !this.pdfReader.readerElement) return 0;
        return this.pdfReader.readerElement.querySelector('.PDFReader-document .page').clientWidth;
    }

    render() {
        const isPrintingDisabled = this.props.viewerPermissions && this.props.viewerPermissions.print === true ? "" : " disable-printing ";

        return (
            <div className={"PDFViewer" + isPrintingDisabled}>
                <PDFControls client={this.props.client} currentPage={this.state.currentPage}
                             totalPages={this.state.document.numPages}
                             onPageChange={this.handleToolbarPageChange} title={this.state.title}
                             attribution="Delivered by Singletrack"
                             scale={this.state.scale} onZoomIn={this.handleZoomIn} onZoomOut={this.handleZoomOut}
                             onAttributionClick={this.handleAttributionClick} ratingOptions={this.state.ratingOptions}
                             loadingProgress={this.state.loadingProgress} documentLoaded={this.state.documentLoaded}
                             pdfReaderScrollbarWidth={this.pdfReaderScrollbarWidth}
                             viewerPermissions={this.props.viewerPermissions}
                             handlePrintClick={this.handlePrintClick} handleDownloadClick={this.handleDownloadClick}
                             handleRatingClick={this.handleRatingClick} getDocPageWidth={this.getDocPageWidth}
                             referrer={this.props.referrer} clientActionId={this.props.clientActionId} handleRelatedResearchClick={this.handleRelatedResearchClick}
                             startTimestamp={this.props.startTimestamp} permsSignature={this.props.permsSignature} />
                {this.props.uri &&
                <PDFReader uri={this.props.uri} toolbarSetPage={this.state.toolbarSetPage} scale={this.state.scale}
                           onDocumentLoaded={this.handleDocumentLoaded} onPageChange={this.handlePageChange}
                           onScroll={this.handleScroll} onError={this.props.onError}
                           onLoadingProgress={this.handleLoadingProgress}
                           onPagesInit={this.handlePagesInit} ref={pdfReader => this.pdfReader = pdfReader}/>
                }
                <Privacy/>
            </div>
        )
    }
}

export default PDFViewer
